function HowItWorks({ onBackClick, onDonateClick, onVolunteerClick, onPartnerClick }) {
    try {
        React.useEffect(() => {
            // Scroll to top when component mounts
            window.scrollTo(0, 0);
            
            // Initialize any animations or effects when the component loads
            if (window.initThreeScene) {
                const cleanup = window.initThreeScene('model-container', 'default');
                
                return () => {
                    if (typeof cleanup === 'function') {
                        cleanup();
                    }
                };
            }
        }, []);

        const handleBackClick = (e) => {
            e.preventDefault();
            if (onBackClick) onBackClick();
        };

        const steps = [
            {
                icon: "fa-trash-alt",
                title: "E-Waste Collection",
                description: "We collect discarded electronic devices from businesses, educational institutions, and individuals that would otherwise contribute to growing e-waste problems.",
                details: "Our specialized collection teams ensure proper handling and documentation of all donated hardware, following strict data security protocols to protect sensitive information.",
                color: "green"
            },
            {
                icon: "fa-tools",
                title: "Refurbishment Process",
                description: "Our skilled technicians test, repair, and refurbish donated hardware, giving these devices a second life while minimizing environmental impact.",
                details: "Components are thoroughly cleaned, tested, and upgraded when possible. We use specialized diagnostic tools to ensure all refurbished systems meet performance standards.",
                color: "blue"
            },
            {
                icon: "fa-solar-panel",
                title: "Renewable Power Integration",
                description: "All our computing infrastructure is powered by a combination of solar panels, wind turbines, and advanced battery storage systems.",
                details: "Our energy management system optimizes power distribution, ensuring maximum efficiency and minimal carbon footprint even during peak computing demands.",
                color: "yellow"
            },
            {
                icon: "fa-network-wired",
                title: "Network Deployment",
                description: "We distribute the sustainable computing power to educational institutions in need, creating a global network of green computing resources.",
                details: "Each deployment includes installation, training, and ongoing support to ensure recipient organizations can maximize the value of these resources.",
                color: "purple"
            },
            {
                icon: "fa-graduation-cap",
                title: "Educational Support",
                description: "We provide training and educational resources to help institutions make the most of their new computing power.",
                details: "Our curriculum includes basic computer literacy, advanced programming, and system administration tailored to the specific needs of each institution.",
                color: "red"
            },
            {
                icon: "fa-chart-line",
                title: "Impact Measurement",
                description: "We track and report the environmental and educational impact of our network, ensuring transparency and continuous improvement.",
                details: "Our impact dashboard provides real-time metrics on carbon savings, educational outcomes, and system performance across the entire network.",
                color: "teal"
            }
        ];

        const partnerships = [
            {
                name: "Educational Institutions",
                description: "We partner with schools, colleges, and universities worldwide to deploy sustainable computing solutions and support educational initiatives.",
                icon: "fa-school",
                testimonial: {
                    quote: "The sustainable computing lab has transformed how our students learn about technology while reinforcing our commitment to environmental stewardship.",
                    author: "Dr. Maria Rodriguez",
                    title: "Principal, Green Valley Academy"
                }
            },
            {
                name: "Corporate Donors",
                description: "Businesses contribute their retired hardware, funding, and technical expertise to support our mission and reduce their environmental footprint.",
                icon: "fa-building",
                testimonial: {
                    quote: "Our partnership with SCN has not only helped us meet our sustainability goals but has created meaningful social impact through technology reuse.",
                    author: "James Chen",
                    title: "CSO, TechGlobal Inc."
                }
            },
            {
                name: "Environmental Organizations",
                description: "We collaborate with environmental groups to ensure our operations meet the highest standards of sustainability and ecological responsibility.",
                icon: "fa-leaf",
                testimonial: {
                    quote: "SCN demonstrates how technology and environmental stewardship can work hand in hand to create solutions that benefit both people and planet.",
                    author: "Sarah Johnson",
                    title: "Director, EcoFuture Alliance"
                }
            },
            {
                name: "Volunteer Networks",
                description: "Technology professionals and enthusiasts volunteer their time and skills to help refurbish hardware and provide technical support.",
                icon: "fa-hands-helping",
                testimonial: {
                    quote: "Volunteering with SCN lets me use my technical skills to make a real difference, knowing that every device I repair helps students access education.",
                    author: "Miguel Sanchez",
                    title: "Senior Systems Engineer & SCN Volunteer"
                }
            }
        ];

        const impactMetrics = [
            { 
                value: "100+", 
                label: "Tons of e-waste diverted from landfills",
                icon: "fa-recycle"
            },
            { 
                value: "80%", 
                label: "Reduction in carbon footprint compared to new hardware",
                icon: "fa-leaf"
            },
            { 
                value: "100%", 
                label: "Renewable energy powered operations",
                icon: "fa-solar-panel"
            },
            { 
                value: "50,000+", 
                label: "Students with access to sustainable computing resources",
                icon: "fa-user-graduate"
            },
            { 
                value: "25", 
                label: "Countries with active SCN deployments",
                icon: "fa-globe"
            },
            { 
                value: "15,000", 
                label: "Devices refurbished and redeployed annually",
                icon: "fa-laptop"
            }
        ];

        const technologies = [
            {
                name: "Solar Power Systems",
                icon: "fa-sun",
                description: "High-efficiency photovoltaic arrays providing clean energy to our computing clusters"
            },
            {
                name: "Wind Energy Integration",
                icon: "fa-wind",
                description: "Micro wind turbines supplementing solar power in diverse geographical locations"
            },
            {
                name: "Advanced Battery Storage",
                icon: "fa-battery-full",
                description: "Next-generation battery systems ensuring consistent power availability"
            },
            {
                name: "Edge Computing",
                icon: "fa-microchip",
                description: "Distributed processing capabilities that reduce bandwidth needs and energy consumption"
            },
            {
                name: "Smart Power Management",
                icon: "fa-bolt",
                description: "AI-driven systems that optimize energy use based on computational demands"
            },
            {
                name: "Low-Power Computing",
                icon: "fa-server",
                description: "Energy-efficient hardware configurations designed for maximum performance per watt"
            }
        ];

        return (
            <section data-name="how-it-works" className="min-h-screen bg-gray-900 text-white relative overflow-hidden">
                {/* Background Elements */}
                <div className="absolute inset-0 -z-10">
                    <div className="circuit-grid"></div>
                </div>
                
                {/* Floating particles */}
                <div className="absolute top-0 left-0 w-full h-full opacity-10">
                    {Array.from({ length: 30 }).map((_, i) => (
                        <div 
                            key={i}
                            className="absolute rounded-full bg-green-400"
                            style={{
                                width: `${Math.random() * 10 + 5}px`,
                                height: `${Math.random() * 10 + 5}px`,
                                left: `${Math.random() * 100}%`,
                                top: `${Math.random() * 100}%`,
                                animation: `float ${Math.random() * 10 + 5}s linear infinite`,
                                animationDelay: `${Math.random() * 5}s`
                            }}
                        ></div>
                    ))}
                </div>
                
                <div className="container mx-auto px-4 py-16">
                    <button 
                        onClick={handleBackClick}
                        className="mb-8 flex items-center text-green-400 hover:text-green-300 transition-colors back-button"
                        data-name="back-button"
                    >
                        <i className="fas fa-arrow-left mr-2"></i> Back to Home
                    </button>

                    <div className="max-w-6xl mx-auto">
                        {/* Hero Section */}
                        <div className="text-center mb-16" data-name="hero-section">
                            <h1 className="text-5xl md:text-6xl font-bold mb-6">
                                How <span className="text-green-400">It Works</span>
                            </h1>
                            <p className="text-xl text-gray-300 mb-12 max-w-3xl mx-auto">
                                Our innovative model transforms electronic waste into sustainable computing power 
                                for educational institutions worldwide, powered by renewable energy.
                            </p>
                            
                            {/* 3D Model Visualization */}
                            <div className="mb-16">
                                <div 
                                    id="model-container" 
                                    className="h-80 md:h-[500px] rounded-xl overflow-hidden relative mx-auto max-w-4xl"
                                    data-name="3d-model"
                                >
                                    <div className="absolute inset-0 bg-gradient-to-br from-green-500/10 to-blue-500/10"></div>
                                    <div className="glow-effect"></div>
                                </div>
                            </div>
                        </div>

                        {/* Process Steps Section */}
                        <div className="mb-24" data-name="process-steps">
                            <h2 className="text-4xl font-bold mb-12 text-center">
                                Our <span className="text-green-400">Process</span>
                            </h2>
                            
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                                {steps.map((step, index) => (
                                    <div 
                                        key={index}
                                        className="step-card bg-gray-800/50 rounded-xl p-6 border border-gray-700/50 backdrop-blur-sm transform transition-all duration-500 hover:scale-105"
                                        data-name={`step-${index}`}
                                    >
                                        <div className={`bg-gradient-to-br from-${step.color}-400/20 to-${step.color}-600/20 w-16 h-16 rounded-full flex items-center justify-center mb-6`}>
                                            <i className={`fas ${step.icon} text-${step.color}-400 text-2xl`}></i>
                                        </div>
                                        <h3 className="text-xl font-bold mb-4">{step.title}</h3>
                                        <p className="text-gray-300 mb-4">{step.description}</p>
                                        <p className="text-gray-400 text-sm">{step.details}</p>
                                    </div>
                                ))}
                            </div>
                        </div>
                        
                        {/* Technologies Section */}
                        <div className="mb-24 bg-gray-800/30 rounded-3xl p-10 backdrop-blur-sm" data-name="technologies-section">
                            <h2 className="text-4xl font-bold mb-12 text-center">
                                Key <span className="text-green-400">Technologies</span>
                            </h2>
                            
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                                {technologies.map((tech, index) => (
                                    <div 
                                        key={index} 
                                        className="flex items-start p-5 rounded-xl bg-gradient-to-br from-gray-800/50 to-gray-900/50 border border-gray-700/30"
                                        data-name={`tech-${index}`}
                                    >
                                        <div className="bg-green-400/10 rounded-full p-4 mr-4">
                                            <i className={`fas ${tech.icon} text-green-400 text-2xl`}></i>
                                        </div>
                                        <div>
                                            <h3 className="text-xl font-bold mb-2">{tech.name}</h3>
                                            <p className="text-gray-400">{tech.description}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Impact Metrics Section */}
                        <div className="mb-24" data-name="impact-metrics">
                            <h2 className="text-4xl font-bold mb-12 text-center">
                                Our <span className="text-green-400">Impact</span>
                            </h2>
                            
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                                {impactMetrics.map((metric, index) => (
                                    <div 
                                        key={index}
                                        className="impact-card bg-gray-800/50 rounded-xl p-6 border border-gray-700/50 backdrop-blur-sm text-center"
                                        data-name={`impact-${index}`}
                                    >
                                        <div className="inline-flex items-center justify-center rounded-full bg-green-400/10 w-20 h-20 mb-6">
                                            <i className={`fas ${metric.icon} text-green-400 text-3xl`}></i>
                                        </div>
                                        <div className="text-4xl font-bold text-green-400 mb-2">{metric.value}</div>
                                        <p className="text-gray-300">{metric.label}</p>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Partnerships Section with Testimonials */}
                        <div className="mb-24" data-name="partnerships">
                            <h2 className="text-4xl font-bold mb-12 text-center">
                                Our <span className="text-green-400">Partnerships</span>
                            </h2>
                            
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
                                {partnerships.map((partner, index) => (
                                    <div 
                                        key={index}
                                        className="bg-gray-800/50 rounded-xl overflow-hidden border border-gray-700/50 backdrop-blur-sm flex flex-col"
                                        data-name={`partner-${index}`}
                                    >
                                        <div className="p-6 flex items-start">
                                            <div className="bg-gradient-to-br from-green-400/20 to-green-600/20 w-12 h-12 rounded-full flex items-center justify-center mr-4 flex-shrink-0">
                                                <i className={`fas ${partner.icon} text-green-400`}></i>
                                            </div>
                                            <div>
                                                <h3 className="text-xl font-bold mb-2">{partner.name}</h3>
                                                <p className="text-gray-300">{partner.description}</p>
                                            </div>
                                        </div>
                                        
                                        <div className="mt-auto border-t border-gray-700/50 p-6 bg-gray-800/30">
                                            <blockquote className="italic text-gray-300 mb-4">
                                                "{partner.testimonial.quote}"
                                            </blockquote>
                                            <div className="flex items-center">
                                                <div className="w-10 h-10 rounded-full bg-gray-600 flex items-center justify-center mr-3">
                                                    <i className="fas fa-user text-gray-300"></i>
                                                </div>
                                                <div>
                                                    <div className="font-medium">{partner.testimonial.author}</div>
                                                    <div className="text-sm text-gray-400">{partner.testimonial.title}</div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                        
                        {/* Call to Action */}
                        <div className="mt-16 text-center bg-gradient-to-r from-green-500/20 to-blue-500/20 rounded-3xl p-12 backdrop-blur-sm" data-name="cta-section">
                            <h2 className="text-3xl font-bold mb-6">Ready to Make an Impact?</h2>
                            <p className="text-gray-300 mb-8 max-w-2xl mx-auto">
                                Join our mission to transform e-waste into educational opportunities while protecting our planet.
                                Together, we can build a more sustainable and equitable future.
                            </p>
                            <div className="flex flex-wrap justify-center gap-4">
                                <button 
                                    onClick={onDonateClick}
                                    className="btn btn-primary"
                                    data-name="donate-button"
                                >
                                    <i className="fas fa-laptop mr-2"></i>
                                    Donate Hardware
                                </button>
                                <button 
                                    onClick={onVolunteerClick}
                                    className="btn btn-secondary"
                                    data-name="volunteer-button"
                                >
                                    <i className="fas fa-hands-helping mr-2"></i>
                                    Volunteer With Us
                                </button>
                                <button 
                                    onClick={onPartnerClick}
                                    className="btn btn-secondary"
                                    data-name="partner-button"
                                >
                                    <i className="fas fa-building mr-2"></i>
                                    Partner With Us
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        );
    } catch (error) {
        console.error('HowItWorks error:', error);
        reportError(error);
        return null;
    }
}

window.HowItWorks = HowItWorks;

export default HowItWorks;
